import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import StudyActions from "@/components/StudyActions";
import HighlightableText from "@/components/HighlightableText";
import { Loader2, ArrowRight, BookOpen, PenLine } from "lucide-react";

function Paragraphs({ text, className, blockPrefix, highlights }) {
  const parts = (text || "").split(/\n{2,}/).filter((p) => p.trim());
  return (
    <>
      {parts.map((p, i) => (
        <p key={i} className={className}>
          <HighlightableText block={`${blockPrefix}-${i}`} text={p.trim()} highlights={highlights} editable={false} />
        </p>
      ))}
    </>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-20 bg-[#07090E]/85 backdrop-blur-md border-b border-[#1E273D]">
      <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="shared-logo" className="flex items-center gap-2">
          <img src="/openword-logo.png" alt="" className="w-6 h-6" />
          <span className="text-lg text-white">OpenWord AI</span>
        </Link>
        <Link
          to="/login"
          data-testid="shared-signin"
          className="text-sm font-medium text-[#07090E] bg-[#00E5FF] hover:bg-[#00B8CC] rounded-full px-5 h-9 flex items-center transition-colors"
        >
          Read along
        </Link>
      </div>
    </header>
  );
}

function SignupCta({ testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 sm:p-8 text-center space-y-4"
    >
      <div className="flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
        <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Study the passage for yourself
      </div>
      <p className="text-xl text-slate-200 leading-relaxed">
        OpenWord AI is a Scripture-first virtual study companion for personal Bible study — free to begin.
        Read the passage, take notes, and go deeper at your own pace.
      </p>
      <Link
        to="/login"
        data-testid={`${testid}-btn`}
        className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-7 h-12 text-base font-medium transition-colors"
      >
        Read it for yourself <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <p className="text-xs text-slate-500">No cost — and OpenWord AI is a study tool; the Word itself remains the belief system.</p>
    </section>
  );
}

export default function SharedStudy() {
  const { token } = useParams();
  const [data, setData] = useState(undefined); // undefined = loading, null = unavailable
  const pinged = useRef(false); // guard StrictMode's double-invoke so one open = one view

  useEffect(() => {
    api
      .get(`/share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    // Count the open exactly once (separate, non-idempotent endpoint).
    if (!pinged.current) {
      pinged.current = true;
      api.post(`/share/${token}/view`).catch(() => {});
    }
  }, [token]);

  if (data === undefined) {
    return (
      <div className="min-h-screen bg-[#07090E] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen bg-[#07090E]">
        <TopBar />
        <div className="max-w-lg mx-auto px-6 py-24 text-center space-y-6">
          <h1 className="text-3xl text-white">This study isn&apos;t available.</h1>
          <p className="text-slate-400">The link may have been removed by its author.</p>
          <SignupCta testid="shared-cta-error" />
        </div>
      </div>
    );
  }

  const { study, passage, hook, conversation, notes, reflections, shared_by, highlights } = data;
  const verses = passage?.verses || [];

  return (
    <div className="min-h-screen bg-[#07090E]" data-testid="shared-study">
      <TopBar />
      <article className="max-w-3xl mx-auto px-6 py-12 space-y-12">
        {/* Hero */}
        <header className="space-y-4">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
            <span className="text-[#00E5FF]">{study?.passage_reference}</span>
            {passage?.translation_name && (
              <>
                <span>·</span>
                <span>{passage.translation_name}</span>
              </>
            )}
          </div>
          <h1 data-testid="shared-title" className="text-4xl sm:text-5xl text-white leading-tight">
            {study?.title}
          </h1>
          {shared_by && (
            <div data-testid="shared-byline" className="flex items-center gap-1.5 text-sm text-slate-400">
              <PenLine className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.5} /> Shared by {shared_by}
            </div>
          )}
          {hook && (
            <div
              data-testid="shared-hook"
              className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 text-lg text-slate-300 leading-relaxed italic"
            >
              {hook}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <StudyActions study={study} passage={passage} testid="shared-study" />
          </div>
        </header>

        <SignupCta testid="shared-cta-top" />

        {/* Scripture */}
        {verses.length > 0 && (
          <section className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Scripture</div>
            <div className="text-2xl leading-relaxed text-slate-200">
              {verses.map((v, i) => (
                <span key={i}>
                  <sup className="text-[#00E5FF] text-sm mr-1">{v.verse}</sup>
                  <HighlightableText block={`scripture-v${v.verse}`} text={v.text} highlights={highlights} editable={false} />{" "}
                </span>
              ))}
            </div>
            {passage?.copyright && (
              <p className="text-xs text-slate-500 leading-relaxed">{passage.copyright}</p>
            )}
          </section>
        )}

        {study?.overview && (
          <p className="text-base text-slate-400 leading-relaxed">
            <HighlightableText block="overview" text={study.overview} highlights={highlights} editable={false} />
          </p>
        )}

        {/* Teaching sections (read-only) */}
        {(study?.sections || []).map((sec, si) => (
          <section key={si} className="space-y-4">
            {sec.heading && (
              <h2 className="font-sans text-xl font-medium text-slate-200 tracking-tight">{sec.heading}</h2>
            )}
            <div className="text-lg leading-relaxed text-slate-300 space-y-4">
              <Paragraphs text={sec.commentary} blockPrefix={`section-${si}`} highlights={highlights} />
            </div>
          </section>
        ))}

        {study?.summary && (
          <section className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-2">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Summary</div>
            <p className="text-base text-slate-400 leading-relaxed">
              <HighlightableText block="summary" text={study.summary} highlights={highlights} editable={false} />
            </p>
          </section>
        )}

        {study?.exercise && (
          <section className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 space-y-2">
            <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">An exercise</div>
            <p className="text-base text-slate-300 leading-relaxed">
              <HighlightableText block="exercise" text={study.exercise} highlights={highlights} editable={false} />
            </p>
          </section>
        )}

        {/* Reflection answers (opt-in) */}
        {reflections?.length > 0 && (
          <section data-testid="shared-reflections" className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Reflections</div>
            <div className="space-y-4">
              {reflections.map((r, i) => (
                <div key={i} className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-1.5">
                  <p className="text-sm font-medium text-slate-200 leading-snug">
                    <HighlightableText
                      block={`reflect-${r.section_index}-${r.question_index}-q`}
                      text={r.question}
                      highlights={highlights}
                      editable={false}
                    />
                  </p>
                  <p className="text-base text-slate-400 leading-relaxed whitespace-pre-wrap">
                    <HighlightableText
                      block={`reflect-${r.section_index}-${r.question_index}-a`}
                      text={r.answer}
                      highlights={highlights}
                      editable={false}
                    />
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Personal notes (opt-in) */}
        {notes && (
          <section data-testid="shared-notes" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-2">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Notes</div>
            <p className="text-base text-slate-400 leading-relaxed whitespace-pre-wrap">{notes}</p>
          </section>
        )}

        {/* Go Deeper conversation (opt-in) */}
        {conversation?.length > 0 && (
          <section data-testid="shared-conversation" className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Going deeper — the conversation</div>
            <div className="space-y-3">
              {conversation.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex flex-col items-end" : "flex flex-col items-start"}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-[#00E5FF] text-[#07090E]" : "bg-[#0E131F] border border-[#1E273D] text-slate-300"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <SignupCta testid="shared-cta-bottom" />

        <footer className="pt-8 border-t border-[#1E273D] text-center text-sm text-slate-500 leading-relaxed">
          Shared from <span className="text-[#00E5FF]">OpenWord AI</span> — a free, Scripture-first Bible study tool.
          It is a study tool, not a replacement for a pastor, a church, or the Bible itself.
        </footer>
      </article>
    </div>
  );
}
